import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/modules/auth/service/auth.service';
import { AlertDialogComponent } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { clearStaleMsalInteraction } from 'src/app/modules/auth/msal-interaction.util';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Parallel requests can each fail with 401; only ask the user once.
  private sessionExpiredHandled = false;

  constructor(
    private authService: AuthService,
    private dialog: MatDialog
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    const isLoginCall = this.isLoginRequest(req.url);

    const outgoing =
      !isLoginCall && token
        ? req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
          })
        : req;

    return next.handle(outgoing).pipe(
      catchError((error: HttpErrorResponse) => {
        // status 0 is a network/CORS failure, not an expired session.
        if (
          error?.status === 401 &&
          this.isLmsApiRequest(req.url) &&
          !isLoginCall
        ) {
          this.askUserToRelogin();
        } else {
          console.error('HTTP error occurred:', error);
        }
        return throwError(error);
      })
    );
  }

  private isLoginRequest(url: string): boolean {
    return url.includes('Login/tokensso') || url.includes('Login/token');
  }

  private isLmsApiRequest(url: string): boolean {
    return url.startsWith(environment.baseUrl);
  }

  private askUserToRelogin() {
    if (this.sessionExpiredHandled) {
      return;
    }
    this.sessionExpiredHandled = true;

    this.authService.setAuthenticatedStatus(false);
    localStorage.clear();
    clearStaleMsalInteraction();

    try {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        maxWidth: '343px',
        disableClose: true,
        data: {
          msg: 'Your session has expired. Please log in again.',
          isAlert: true,
          okOnly: true,
        },
      });

      dialogRef.afterClosed().subscribe({
        next: () => this.goToLogin(),
        error: () => this.goToLogin(),
      });
    } catch {
      this.goToLogin();
    }
  }

  private goToLogin() {
    window.location.replace('/login');
  }
}
